import { Lock, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CompanyLookup from "@/components/landing/CompanyLookup";
import { SignatoriesFields } from "@/components/landing/order/SignatoriesFields";
import { FieldError, errCls } from "@/lib/validate";
import { useT } from "@/i18n";

const inputCls = "bg-background";
const EEA = ["PL", "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IE", "IS", "IT", "LV", "LI", "LT", "LU", "MT", "NL", "NO", "PT", "RO", "SK", "SI", "ES", "SE"];
const countryName = (code, lang) => { try { return new Intl.DisplayNames([lang], { type: "region" }).of(code) || code; } catch { return code; } };
const DIALS = [["PL", "+48"], ["GB", "+44"], ["DE", "+49"], ["FR", "+33"], ["IT", "+39"], ["ES", "+34"], ["NL", "+31"], ["BE", "+32"], ["AT", "+43"], ["CZ", "+420"], ["SK", "+421"], ["UA", "+380"], ["LT", "+370"], ["LV", "+371"], ["EE", "+372"], ["IE", "+353"], ["SE", "+46"], ["DK", "+45"], ["NO", "+47"], ["FI", "+358"], ["PT", "+351"], ["RO", "+40"], ["HU", "+36"], ["BG", "+359"], ["HR", "+385"], ["CH", "+41"], ["US", "+1"], ["TR", "+90"], ["RU/KZ", "+7"], ["BY", "+375"], ["MD", "+373"], ["AM", "+374"], ["AZ", "+994"], ["GE", "+995"], ["KG", "+996"], ["TJ", "+992"], ["TM", "+993"], ["UZ", "+998"]];
const DIALS_BY_LEN = [...DIALS].map((d) => d[1]).sort((a, b) => b.length - a.length);

export function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div className="space-y-4">
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{label}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

export function OrderFields({ mode, form, set, setForm, errors, clearErr, onRegistry, signatories, setSignatories, emailExtra, companyLocked = false, onLookupStatus }) {
  const { t, lang } = useT();
  const O = t.order;
  const W = O.wizard;
  const isKrs = mode === "existing" && form.legal_form && form.legal_form !== "jdg";
  const isSc = form.legal_form === "sc";
  const sigProps = isSc ? { max: 4, label: "Wspólnicy spółki cywilnej (maks. 4) *", addLabel: "Dodaj wspólnika", defaultRole: "Wspólnik", showRepresents: true } : {};
  const text = (key, testid, label, ph = "", extra = {}) => (
    <Field label={label}>
      <Input data-testid={testid} data-field={key} aria-invalid={!!errors[key]} value={form[key]} onChange={set(key)} placeholder={ph} className={`${inputCls} ${errCls(errors[key])}`} {...extra} />
      <FieldError id={`order-${key}`} msg={errors[key]} />
    </Field>
  );
  const legalForm = (
    <Field label={O.legal_form}>
      <Select value={form.legal_form} onValueChange={(v) => { setForm({ ...form, legal_form: v }); clearErr("legal_form"); }}>
        <SelectTrigger data-testid="order-legal-form-select" data-field="legal_form" aria-invalid={!!errors.legal_form} className={`${inputCls} ${errCls(errors.legal_form)}`}>
          <SelectValue placeholder={O.legal_form_ph} />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(O.legal_forms).filter(([k]) => mode === "existing" || k !== "jdg").map(([k, v]) => (
            <SelectItem key={k} value={k} data-testid={`order-legal-form-option-${k}`}>{v}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <FieldError id="order-legal_form" msg={errors.legal_form} />
    </Field>
  );
  const email = (
    <>
      {text("email", "order-email-input", O.email, O.email_ph, { type: "email" })}
      {emailExtra && <div className="sm:col-span-2">{emailExtra}</div>}
    </>
  );
  const dialMatch = DIALS_BY_LEN.find((d) => (form.phone || "").startsWith(d));
  const dial = dialMatch || "+48";
  const localPhone = dialMatch ? form.phone.slice(dialMatch.length).trim() : (form.phone || "").replace(/^\+/, "");
  const setPhone = (d, n) => { set("phone")({ target: { value: `${d} ${String(n).replace(/[^\d\s]/g, "").trim()}`.trim() } }); clearErr("phone"); };
  const phone = (
    <Field label={O.phone}>
      <div className="flex gap-2">
        <Select value={dial} onValueChange={(d) => setPhone(d, localPhone)}>
          <SelectTrigger data-testid="order-phone-dial" aria-label="Prefiks kraju" className={`${inputCls} w-[7.5rem] shrink-0`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {DIALS.map(([c, d]) => <SelectItem key={c} value={d} data-testid={`order-phone-dial-${c}`}>{c} {d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input data-testid="order-phone-input" data-field="phone" aria-invalid={!!errors.phone} inputMode="tel" value={localPhone} onChange={(e) => setPhone(dial, e.target.value)} placeholder={O.phone_ph} className={`${inputCls} ${errCls(errors.phone)}`} />
      </div>
      <FieldError id="order-phone" msg={errors.phone} />
    </Field>
  );
  const countries = EEA.map((c) => [c, countryName(c, lang)]).sort((a, b) => (a[0] === "PL" ? -1 : b[0] === "PL" ? 1 : a[1].localeCompare(b[1], lang)));
  const mailing = (
    <>
      <div className="sm:col-span-2">
        {text("mailing_address", "order-mailing-address-input", O.mailing_address, O.mailing_address_ph)}
        <p className="text-[11px] text-muted-foreground mt-1.5 leading-relaxed">{W.mailing_hint}</p>
      </div>
      <Field label={W.mailing_country}>
        <Select value={form.mailing_country || "PL"} onValueChange={(v) => setForm({ ...form, mailing_country: v })}>
          <SelectTrigger data-testid="order-mailing-country-select" className={inputCls}><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-72">
            {countries.map(([c, n]) => <SelectItem key={c} value={c} data-testid={`order-mailing-country-option-${c}`}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
    </>
  );
  const notes = (
    <div className="sm:col-span-2">
      <Field label={O.notes}><Textarea data-testid="order-notes-input" rows={3} value={form.notes} onChange={set("notes")} placeholder={O.notes_ph} className={`${inputCls} resize-none`} /></Field>
    </div>
  );
  const _today = new Date().toISOString().slice(0, 10);
  const startDate = <Field label={O.start_date}><Input data-testid="order-start-date-input" type="date" min={_today} value={form.start_date || _today} onChange={set("start_date")} className={inputCls} /></Field>;

  if (mode === "existing") {
    return (
      <>
        <Section label={O.section_company}>
          <Field label={W.nip_required}>
            <Input data-testid="order-nip-input" data-field="nip" aria-invalid={!!errors.nip} value={form.nip} onChange={set("nip")} placeholder={O.nip_ph} className={`${inputCls} ${errCls(errors.nip)}`} />
            <FieldError id="order-nip" msg={errors.nip} />
            <CompanyLookup nip={form.nip} testId="order-company-lookup" onFill={onRegistry} onStatus={onLookupStatus} auto />
            <p className="text-[11px] text-muted-foreground mt-1.5 leading-relaxed">{W.nip_lookup_hint}</p>
          </Field>
          <Field label={t.lookup.regon_label}>
            <Input data-testid="order-regon-input" data-field="regon" value={form.regon || ""} onChange={set("regon")} placeholder="123456789" className={inputCls} />
            <CompanyLookup nip={form.regon} by="regon" testId="order-company-lookup-regon" onFill={onRegistry} onStatus={onLookupStatus} auto />
            <p className="text-[11px] text-muted-foreground mt-1.5 leading-relaxed">{t.lookup.regon_lookup_hint}</p>
          </Field>
          <Field label={companyLocked ? <span className="inline-flex items-center gap-1.5"><Lock size={11} /> {O.company}</span> : O.company}>
            <Input data-testid="order-company-input" data-field="company" aria-invalid={!!errors.company} readOnly={companyLocked} value={form.company}
              onChange={companyLocked ? undefined : set("company")} placeholder={companyLocked ? W.company_locked_ph : O.company_ph}
              className={`${inputCls} ${companyLocked ? "opacity-80 cursor-not-allowed" : ""} ${errCls(errors.company)}`} />
            <FieldError id="order-company" msg={errors.company} />
            {companyLocked && <p className="text-[11px] text-muted-foreground mt-1.5 leading-relaxed">{W.company_locked_hint}</p>}
          </Field>
          {legalForm}
          {text("krs", "order-krs-input", O.krs, O.krs_ph)}
          <div className="sm:col-span-2">
            <Field label={companyLocked
              ? <span className="inline-flex items-center gap-1.5"><Lock size={11} /> {W.registered_locked}</span>
              : W.registered_locked}>
              <Input data-testid="order-registered-address-input" data-field="registered_address" readOnly={companyLocked} value={form.registered_address}
                onChange={companyLocked ? undefined : set("registered_address")} placeholder={companyLocked ? W.registered_locked_ph : O.mailing_address_ph}
                className={`${inputCls} ${companyLocked ? "opacity-80 cursor-not-allowed" : ""}`} />
              {!companyLocked && <p className="text-[11px] text-brand-red mt-1.5 leading-relaxed">{W.registry_unavailable}</p>}
            </Field>
          </div>
        </Section>
        <Section label={O.section_contact}>
          {isKrs
            ? <div className="sm:col-span-2"><SignatoriesFields signatories={signatories} onChange={setSignatories} errors={errors} clearErr={clearErr} {...sigProps} /></div>
            : text("representative", "order-representative-input", O.representative, O.representative_ph)}
          {startDate}
          {email}
          {phone}
          {mailing}
          {notes}
        </Section>
      </>
    );
  }
  if (mode === "new_jdg") {
    return (
      <Section label={O.section_contact}>
        {text("company", "order-company-input", W.person_name, O.representative_ph)}
        {text("pesel", "order-pesel-input", W.pesel, W.pesel_ph, { inputMode: "numeric" })}
        {text("nip", "order-nip-input", W.nip_optional, O.nip_ph)}
        {startDate}
        {email}
        {phone}
        {mailing}
        {notes}
      </Section>
    );
  }
  if (mode === "new_krs_deed") {
    return (
      <>
        <Section label={O.section_company}>
          {text("company", "order-company-input", W.entity_name, W.entity_name_ph)}
          {legalForm}
        </Section>
        <Section label={O.section_contact}>
          {isKrs
            ? <div className="sm:col-span-2"><SignatoriesFields signatories={signatories} onChange={setSignatories} errors={errors} clearErr={clearErr} {...sigProps} /></div>
            : text("representative", "order-representative-input", O.representative, O.representative_ph)}
          {startDate}
          {email}
          {phone}
          {mailing}
          {notes}
        </Section>
        <div data-testid="order-deed-info" className="rounded-xl border border-brand-yellow/60 bg-brand-yellow/10 p-4 text-sm leading-relaxed">
          <p className="font-semibold inline-flex items-center gap-2"><Mail size={15} /> {W.deed_info} <a data-testid="order-deed-mail-link" href="mailto:biuro@twojebiuro.waw.pl" className="underline underline-offset-2 text-brand-teal">biuro@twojebiuro.waw.pl</a></p>
          <p className="text-muted-foreground mt-2 text-xs">{W.deed_upload}{" "}
            <a data-testid="order-deed-upload-link" href="/biuro-wirtualne-warszawa/wyslij-podpisany-dokument" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 text-brand-teal font-semibold">{W.deed_upload_link}</a>
          </p>
          {W.deed_info_2 && <p className="text-muted-foreground mt-1.5 text-xs">{W.deed_info_2}</p>}
        </div>
      </>
    );
  }
  return (
    <>
      <Section label={O.section_company}>
        {text("company", "order-company-input", W.company_registered, W.entity_name_ph)}
        {legalForm}
      </Section>
      <Section label={O.section_contact}>
        <div className="sm:col-span-2">{text("representative", "order-representative-input", W.representative_new, O.representative_ph)}</div>
        {email}
        {phone}
      </Section>
    </>
  );
}
